import type { ConvertOptions } from "@kensio/pinyinjs";

/**
 * The conversion settings, and the controls that move them.
 *
 * One entry per field of the package's `ConvertOptions`, but described for
 * somebody learning or teaching Chinese rather than for somebody calling the
 * function: the label on the third-tone control says what it does to 你好,
 * not that it is `sandhi.thirdTone`.
 *
 * The defaults are the package's own, which are also the right ones for this
 * audience — standard orthography, as 汉语拼音正词法基本规则 defines it.
 */

/**
 * Every setting the tools expose, with none of them optional.
 *
 * `ConvertOptions` makes each field optional, because leaving one out is how a
 * caller asks for the default. A set of controls needs the opposite: a radio
 * group has to know which of its values is currently on. {@link asConvertOptions}
 * converts back.
 */
export interface ToolOptions {
  readonly locale: "zh-CN" | "zh-TW";
  readonly notation: "marks" | "numbers" | "superscript" | "none";
  readonly apostrophe: "always" | "standard" | "never";
  readonly capitals: "auto" | "proper" | "none";
  readonly punctuation: "latin" | "keep";
  readonly grouping: boolean;
  readonly numbers: "read" | "keep";
  readonly yiBu: boolean;
  readonly thirdTone: boolean;
}

/**
 * What each setting is allowed to be, in the order the panel offers them.
 *
 * The behaviour half of the settings panel. The copy, meaning what each choice
 * is called and the sentence under each row, is the page's, in
 * `articles/data/pinyin_settings.yaml`, because it changes for editorial
 * reasons rather than for behavioural ones and because the panel is markup
 * now rather than something the browser builds on arrival.
 *
 * `satisfies` against `ToolOptions` is what makes this worth having in one
 * place: a value no field can hold is a build failure here, where the table
 * this replaces listed its values beside their labels and agreed with the
 * interface only by inspection.
 *
 * The order is the panel's, and it is checked against the built page rather
 * than merely intended: see `tool-settings.built.iso.test.ts`.
 */
export const optionValues = {
  notation: ["marks", "numbers", "superscript", "none"],
  grouping: [true, false],
  capitals: ["auto", "proper", "none"],
  punctuation: ["latin", "keep"],
  numbers: ["read", "keep"],
  yiBu: [true, false],
  thirdTone: [true, false],
  apostrophe: ["always", "standard", "never"],
  locale: ["zh-CN", "zh-TW"],
} as const satisfies {
  readonly [Field in keyof ToolOptions]: readonly ToolOptions[Field][];
};

/** The settings, as a list of fields and what each may be. */
export const optionFields: readonly {
  readonly field: keyof ToolOptions;
  readonly values: readonly (string | boolean)[];
}[] = Object.entries(optionValues).map(([field, values]) => ({
  field: field as keyof ToolOptions,
  values,
}));

/**
 * What the tools start on.
 */
export const defaultOptions: ToolOptions = {
  locale: "zh-CN",
  notation: "marks",
  apostrophe: "always",
  capitals: "auto",
  punctuation: "latin",
  grouping: true,
  numbers: "read",
  yiBu: true,
  thirdTone: false,
};

/**
 * The settings as the package wants them, with anything left at its default
 * left out.
 *
 * The trimming matters for `sandhi`, which is merged with its defaults rather
 * than replacing them: passing `{}` and passing nothing have to mean the same
 * thing here as they do in the package.
 */
export function asConvertOptions(options: ToolOptions): ConvertOptions {
  const changed: Record<string, unknown> = {};

  for (const field of [
    "locale",
    "notation",
    "apostrophe",
    "capitals",
    "punctuation",
    "grouping",
    "numbers",
  ] as const) {
    if (options[field] !== defaultOptions[field]) {
      changed[field] = options[field];
    }
  }

  const sandhi = {
    ...(options.yiBu !== defaultOptions.yiBu && { yiBu: options.yiBu }),
    ...(options.thirdTone !== defaultOptions.thirdTone && {
      thirdTone: options.thirdTone,
    }),
  };
  if (Object.keys(sandhi).length > 0) {
    changed["sandhi"] = sandhi;
  }

  return changed;
}

/** Whether every setting is where it started. */
export function isDefault(options: ToolOptions): boolean {
  return Object.keys(asConvertOptions(options)).length === 0;
}
